// ProVideoEditorV2.js - Professional Video Editor with AI Chat Assistant
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getApiBase, credentialsModeForApiUrl } from '../backendProbe.js';
import './ProVideoEditorV2.css';

export default function ProVideoEditorV2({ isOpen, onClose, onSwitchToImageEditor, onSwitchToChat, embedded = false, openScriptToVideo = false, onScriptToVideoOpened }) {
  const isEmbedded = Boolean(embedded);
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement && document.fullscreenElement === containerRef.current);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;
    try {
      if (isFullscreen) {
        await document.exitFullscreen();
      } else {
        await containerRef.current.requestFullscreen();
      }
    } catch (err) {
      console.warn('Fullscreen error:', err);
    }
  }, [isFullscreen]);

  // Timeline state
  const [tracks, setTracks] = useState([
    { id: 'video1', name: 'Main Camera', type: 'video', clips: [], locked: false, visible: true },
    { id: 'video2', name: 'Second Camera', type: 'video', clips: [], locked: false, visible: true },
    { id: 'video3', name: 'Drone', type: 'video', clips: [], locked: false, visible: true },
    { id: 'audio1', name: 'Music', type: 'audio', clips: [], locked: false, visible: true },
    { id: 'audio2', name: 'Dialogue', type: 'audio', clips: [], locked: false, visible: true },
    { id: 'titles', name: 'Titles & Text', type: 'text', clips: [], locked: false, visible: true },
  ]);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(180); // 3 minutes default
  const [playing, setPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [selectedClip, setSelectedClip] = useState(null);
  const [selectedTool, setSelectedTool] = useState('select');

  // AI Chat Assistant
  const [aiMessages, setAiMessages] = useState([
    { id: 1, sender: 'ai', text: "👋 Hi! Import video, paste a script and click **Create video from script**, or ask me to edit—scenes, colour, speech, captions, reframe, and more." }
  ]);
  const [aiInput, setAiInput] = useState('');
  const [aiProcessing, setAiProcessing] = useState(false);
  const [capabilitiesOpen, setCapabilitiesOpen] = useState(false);
  const [aiWhatsNewOpen, setAiWhatsNewOpen] = useState(false);
  const [whatCanYouCreateOpen, setWhatCanYouCreateOpen] = useState(false);
  const [transitionsEffectsOpen, setTransitionsEffectsOpen] = useState(false);
  const [titlesGraphicsOpen, setTitlesGraphicsOpen] = useState(false);
  const [colourGradingOpen, setColourGradingOpen] = useState(false);
  const [audioFeaturesOpen, setAudioFeaturesOpen] = useState(false);
  const [exportShareOpen, setExportShareOpen] = useState(false);
  const [toolsReferenceOpen, setToolsReferenceOpen] = useState(false);
  const aiChatRef = useRef(null);

  // Auto-scroll AI chat to bottom when new messages arrive
  useEffect(() => {
    if (aiChatRef.current)
      aiChatRef.current.scrollTop = aiChatRef.current.scrollHeight;
  }, [aiMessages.length, aiProcessing]);

  // Effects & Filters
  const [effects, setEffects] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    temperature: 0,
    stabilization: false,
    slowMotion: false,
    speedRamp: false,
  });

  // Premiere-style export options (baked into render)
  const [exportSpeed, setExportSpeed] = useState(1);
  const [exportReverse, setExportReverse] = useState(false);
  const [fadeInSec, setFadeInSec] = useState(0);
  const [fadeOutSec, setFadeOutSec] = useState(0);
  const [titleOverlays, setTitleOverlays] = useState([]);
  const [exportStabilize, setExportStabilize] = useState(false);
  const [exportFormat, setExportFormat] = useState('mp4');
  const [exportQuality, setExportQuality] = useState('high');
  const [transitionType, setTransitionType] = useState('fade');

  const videoRef = useRef(null);
  const timelineTrackContentRef = useRef(null);
  const clipDragRef = useRef(null);
  const [uploadedClips, setUploadedClips] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [renderProgress, setRenderProgress] = useState(0);
  const [markers, setMarkers] = useState([]);
  const audioRefs = useRef([]); // References to audio elements for mixing
  const [audioVolumes, setAudioVolumes] = useState({}); // Volume control for each audio track
  const [trackMuted, setTrackMuted] = useState({}); // { [trackId]: true } = muted
  const timeUpdateThrottleRef = useRef(0);

  // Multi-cam sync
  const [multiCamMode, setMultiCamMode] = useState(false);
  const [syncedCameras, setSyncedCameras] = useState([]);

  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);
  const [timelineOpen, setTimelineOpen] = useState(true);
  const [typingOptionsOpen, setTypingOptionsOpen] = useState(false);
  const typingOptionsRef = useRef(null);
  const [transcript, setTranscript] = useState('');
  const [scriptToVideoModel, setScriptToVideoModel] = useState('veo3'); // 'veo3' | 'tts'
  const [scriptToVideoVoice, setScriptToVideoVoice] = useState('alloy');
  const aiInputRef = useRef(null);
  const musicInputRef = useRef(null);
  const copiedClipRef = useRef(null); // { clip, trackId } for Ctrl+V paste
  const tracksRef = useRef(tracks);
  const historyRef = useRef([]); // undo stack (max 20)
  const redoRef = useRef([]);
  const [undoCount, setUndoCount] = useState(0);
  const [redoCount, setRedoCount] = useState(0);

  useEffect(() => { tracksRef.current = tracks; }, [tracks]);

  // When opened from "Create video from script", focus the AI typing box
  useEffect(() => {
    if (isOpen && openScriptToVideo && aiInputRef.current) {
      aiInputRef.current.focus();
      if (typeof onScriptToVideoOpened === 'function') onScriptToVideoOpened();
    }
  }, [isOpen, openScriptToVideo, onScriptToVideoOpened]);

  // Close typing options panel when clicking outside
  useEffect(() => {
    if (!typingOptionsOpen) return;
    const handleClick = (e) => {
      if (typingOptionsRef.current && !typingOptionsRef.current.contains(e.target) && !e.target.closest('.ai-typing-bar-toggle-btn')) {
        setTypingOptionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [typingOptionsOpen]);

  const pushUndoState = useCallback(() => {
    const state = JSON.parse(JSON.stringify(tracksRef.current));
    historyRef.current.push(state);
    if (historyRef.current.length > 20) historyRef.current.shift();
    redoRef.current = [];
    setUndoCount(historyRef.current.length);
    setRedoCount(0);
  }, []);

  const undo = useCallback(() => {
    if (historyRef.current.length === 0) return;
    redoRef.current.push(JSON.parse(JSON.stringify(tracksRef.current)));
    setTracks(historyRef.current.pop());
    setUndoCount(historyRef.current.length);
    setRedoCount(redoRef.current.length);
    setSelectedClip(null);
  }, []);

  const redo = useCallback(() => {
    if (redoRef.current.length === 0) return;
    historyRef.current.push(JSON.parse(JSON.stringify(tracksRef.current)));
    setTracks(redoRef.current.pop());
    setUndoCount(historyRef.current.length);
    setRedoCount(redoRef.current.length);
    setSelectedClip(null);
  }, []);

  const SNAP_GRID_SEC = 0.5;
  const snapToGrid = (t) => Math.round((t || 0) / SNAP_GRID_SEC) * SNAP_GRID_SEC;

  // Sync audio element volumes when track mute or per-clip volume changes
  useEffect(() => {
    const audioClips = uploadedClips.filter(c => c.type === 'audio');
    audioClips.forEach((audioClip, idx) => {
      const el = audioRefs.current[idx];
      if (!el) return;
      const trackWithClip = tracks.find(t => t.clips.some(c => c.id === audioClip.id));
      const muted = trackWithClip && trackMuted[trackWithClip.id];
      el.volume = muted ? 0 : (audioVolumes[audioClip.id] ?? 0.7);
    });
  }, [trackMuted, audioVolumes, tracks, uploadedClips]);

  // Copy/paste clips: Ctrl+C / Ctrl+V (ignore when typing in input/textarea)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.target.closest('input, textarea, [contenteditable="true"]')) return;
      const isCopy = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c';
      const isPaste = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v';
      if (isCopy && selectedClip) {
        const track = tracks.find(t => t.clips.some(c => c.id === selectedClip.id));
        if (track) {
          copiedClipRef.current = { clip: selectedClip, trackId: track.id };
          e.preventDefault();
        }
      } else if (isPaste && copiedClipRef.current) {
        const { clip, trackId } = copiedClipRef.current;
        const track = tracks.find(t => t.id === trackId);
        if (!track || track.type !== (clip.type || 'video')) return;
        e.preventDefault();
        pushUndoState();
        const start = snapToGrid(currentTime);
        const dur = clip.clipDuration ?? clip.duration ?? 10;
        const newClip = {
          ...clip,
          id: `${clip.id.replace(/-copy-\d+$/, '')}-copy-${Date.now()}`,
          startTime: start,
          clipDuration: dur,
          sourceStart: clip.sourceStart ?? 0,
          sourceEnd: clip.sourceEnd ?? dur,
        };
        setTracks(prev => prev.map(t => {
          if (t.id !== trackId) return t;
          const sorted = [...t.clips, newClip].sort((a, b) => (a.startTime ?? 0) - (b.startTime ?? 0));
          return { ...t, clips: sorted };
        }));
        setSelectedClip(newClip);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedClip, tracks, currentTime, pushUndoState]);

  /** Same as main chat: empty on localhost → relative `/video/...` (CRA proxy); configured URL in production. */
  const apiBase = () => getApiBase();

  const getCurrentVideoClip = () => {
    const videoClips = uploadedClips.filter(c => c.type === 'video');
    if (selectedClip && videoClips.some(c => c.id === selectedClip.id || c.id === String(selectedClip.id).replace(/-split-\d+$/, ''))) {
      const base = videoClips.find(c => c.id === selectedClip.id || String(selectedClip.id).replace(/-split-\d+$/, '') === c.id);
      return base || videoClips[0];
    }
    return videoClips[0];
  };

  const getVideoFileForApi = async (clip) => {
    if (!clip) return null;
    if (clip.file && clip.file instanceof File) return clip.file;
    if (clip.url && (clip.url.startsWith('blob:') || clip.url.startsWith('http'))) {
      const res = await fetch(clip.url);
      const blob = await res.blob();
      return new File([blob], clip.name || 'video.mp4', { type: blob.type || 'video/mp4' });
    }
    return null;
  };

  /** Server-generated MP4 URL → clip list + timeline so the empty preview hides (same idea as image editor after Generate). */
  const addRemoteVideoClip = useCallback((dataVideoUrl, { name = 'Video', idPrefix = 'remote' } = {}) => {
    if (!dataVideoUrl || typeof dataVideoUrl !== 'string') return;
    const base = getApiBase();
    const prefix = base ? (dataVideoUrl.startsWith('/') ? base + dataVideoUrl : `${base}/${dataVideoUrl}`) : (dataVideoUrl.startsWith('/') ? dataVideoUrl : `/${dataVideoUrl}`);
    const videoPath = dataVideoUrl.startsWith('http') ? dataVideoUrl : prefix;
    const fullUrl = `${videoPath}${videoPath.includes('?') ? '&' : '?'}t=${Date.now()}`;
    const newClip = {
      id: `${idPrefix}-${Date.now()}`,
      name,
      url: fullUrl,
      type: 'video',
      duration: 60,
    };
    setUploadedClips((prev) => [newClip, ...prev]);
    setTracks((prevTracks) => {
      const firstVideoTrack = prevTracks.find((t) => t.type === 'video');
      if (!firstVideoTrack) return prevTracks;
      const dur = newClip.duration || 60;
      const startTime = firstVideoTrack.clips.reduce(
        (max, c) => Math.max(max, (c.startTime ?? 0) + (c.clipDuration ?? c.duration ?? 0)),
        0
      );
      return prevTracks.map((t) => {
        if (t.id !== firstVideoTrack.id) return t;
        return {
          ...t,
          clips: [
            ...t.clips,
            {
              ...newClip,
              trackId: t.id,
              startTime,
              clipDuration: dur,
              sourceStart: 0,
              sourceEnd: dur,
            },
          ],
        };
      });
    });
    setCurrentTime(0);
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.load();
      }
    }, 50);
  }, []);

  // AI Command Processing
  const processAICommand = async (command) => {
    setAiProcessing(true);
    const lowerCommand = (command || '').toLowerCase();

    const userMsg = { id: Date.now(), sender: 'user', text: command };
    setAiMessages(prev => [...prev, userMsg]);

    const addAiReply = (text) => {
      setAiMessages(prev => [...prev, { id: Date.now() + Math.random(), sender: 'ai', text }]);
      setTimeout(() => { if (aiChatRef.current) aiChatRef.current.scrollTop = aiChatRef.current.scrollHeight; }, 100);
    };

    const base = apiBase();
    const videoClip = getCurrentVideoClip();

    try {

    // Handle editor switching commands
    if (lowerCommand.includes('switch to image') || lowerCommand.includes('open image editor') || lowerCommand.includes('image editor')) {
      addAiReply("🎨 Switching to Professional Image Editor...");
      setAiProcessing(false);
      setTimeout(() => { if (onSwitchToImageEditor) onSwitchToImageEditor(); }, 500);
      return;
    } else if (lowerCommand.includes('switch to chat') || lowerCommand.includes('open chat') || lowerCommand.includes('back to chat') || lowerCommand.includes('zolaid computer')) {
      addAiReply("💬 Returning to Zolaid...");
      setAiProcessing(false);
      setTimeout(() => { if (onSwitchToChat) onSwitchToChat(); }, 500);
      return;
    }

    // ----- Real AI features (backend APIs) -----
    const isSceneDetect = /scene\s*edit\s*detection|scene\s*detection|scenes|run scene|add cuts at each scene/.test(lowerCommand);
    const isAutoColour = /auto\s*colour|auto color|fix exposure|colour correction/.test(lowerCommand);
    const isSpeechToText = /speech\s*to\s*text|transcript|captions|generate caption|generate transcript/.test(lowerCommand);
    const isDescribeFind = /describe\s*&\s*find|describe and find|search my footage|find clips matching|find clips in my media/.test(lowerCommand);
    const isColourMatch = /colour\s*match|color match|match the look|match colours/.test(lowerCommand);
    const isRemix = /remix|retime music|music to match my video/.test(lowerCommand);
    const isTranslate = /translate\s*caption|caption translate|auto-translate caption|translate to/.test(lowerCommand);
    const isTagAudio = /tag\s*audio|audio tag|music, dialogue, sfx|audio category/.test(lowerCommand);
    const isScriptToVideo = /create\s*video\s*from\s*(?:this\s*)?script|script\s*to\s*video|generate\s*video\s*from\s*script/.test(lowerCommand);
    const notImplemented = /object\s*mask|generative\s*extend|morph\s*cut|auto\s*ducking|text-based\s*edit|text-based editing/.test(lowerCommand);

    if (isScriptToVideo) {
      const scriptMatch = command.match(/create\s*video\s*from\s*(?:this\s*)?script\s*[:\n]\s*([\s\S]+)/i)
        || command.match(/script\s*to\s*video\s*[:\n]\s*([\s\S]+)/i)
        || command.match(/generate\s*video\s*from\s*script\s*[:\n]\s*([\s\S]+)/i);
      const script = (scriptMatch ? scriptMatch[1].trim() : "").trim().slice(0, 4096);
      if (!script) {
        addAiReply("📜 Type or paste your script in the box below and click **Create video from script**.");
        setAiProcessing(false);
        return;
      }
      const modelLabel = scriptToVideoModel === 'veo3' ? 'Veo 3 (VO3)' : 'TTS + static';
      addAiReply(`🎬 Creating video from your script (${modelLabel}). This can take 1–2 minutes — please wait.`);
      try {
        const ep = `${base}/video/script-to-video`;
        const res = await fetch(ep, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: credentialsModeForApiUrl(ep),
          body: JSON.stringify({
            script,
            model: scriptToVideoModel === 'tts' ? 'tts-1' : undefined,
            voice: scriptToVideoVoice,
            preferTts: scriptToVideoModel === 'tts',
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.videoUrl) {
          let err = data.error || "Script-to-video failed.";
          const origin = typeof window !== 'undefined' ? window.location?.origin : '';
          if ((res.status === 404 || res.status === 0) && !base && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(origin || ''))
            err += " Start the backend (port 8080); the dev server proxies /video to it.";
          else if ((res.status === 404 || res.status === 0) && base && origin && base === origin && !/localhost|127\.0\.0\.1/i.test(origin))
            err += " Set REACT_APP_API_URL to your backend URL and rebuild.";
          addAiReply("❌ " + err);
          setAiProcessing(false);
          return;
        }
        addRemoteVideoClip(data.videoUrl, { name: 'AI script video', idPrefix: 'script-video' });
        addAiReply("✅ Video created from your script! It’s on the preview and timeline. You can trim, add music, or export.");
      } catch (e) {
        addAiReply("❌ " + (e.message || "Network error."));
      }
      setAiProcessing(false);
      return;
    }

    if (notImplemented) {
      addAiReply("⚠️ This feature isn’t available in-browser yet. Export your video and use a desktop editor (e.g. Premiere, DaVinci Resolve) for Object Mask, Generative Extend, Morph Cut, Auto Ducking, or Text-Based Edit.");
      setAiProcessing(false);
      return;
    }

    if (isSceneDetect && videoClip) {
      try {
        const file = await getVideoFileForApi(videoClip);
        if (!file) { addAiReply("❌ Please upload a video clip first."); setAiProcessing(false); return; }
        const form = new FormData(); form.append('video', file);
        const epSd = `${base}/video/scene-detect`;
        const res = await fetch(epSd, { method: 'POST', body: form, credentials: credentialsModeForApiUrl(epSd) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { addAiReply("❌ Scene detection failed: " + (data.error || res.statusText)); setAiProcessing(false); return; }
        const scenes = data.scenes || [];
        setMarkers(scenes.map((t, i) => ({ id: `scene-${i}`, time: Math.round(t), label: `Scene ${i + 1}`, type: 'scene' })));
        addAiReply(`✂️ Found ${scenes.length} scene change(s). Markers added to the timeline.`);
      } catch (e) {
        addAiReply("❌ Scene detection error: " + (e.message || 'Network error'));
      }
      setAiProcessing(false);
      return;
    }

    if (isAutoColour && videoClip) {
      try {
        const file = await getVideoFileForApi(videoClip);
        if (!file) { addAiReply("❌ Please upload a video clip first."); setAiProcessing(false); return; }
        const form = new FormData(); form.append('video', file);
        const epAc = `${base}/video/auto-colour`;
        const res = await fetch(epAc, { method: 'POST', body: form, credentials: credentialsModeForApiUrl(epAc) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { addAiReply("❌ Auto colour failed: " + (data.error || res.statusText)); setAiProcessing(false); return; }
        setEffects(prev => ({ ...prev, brightness: data.brightness || 100, contrast: data.contrast || 100, saturation: data.saturation || 100 }));
        addAiReply(`🎨 Auto colour applied (brightness ${data.brightness}, contrast ${data.contrast}, saturation ${data.saturation}).`);
      } catch (e) {
        addAiReply("❌ Auto colour error: " + (e.message || 'Network error'));
      }
      setAiProcessing(false);
      return;
    }

    if (isSpeechToText && videoClip) {
      try {
        const file = await getVideoFileForApi(videoClip);
        if (!file) { addAiReply("❌ Please upload a video clip first."); setAiProcessing(false); return; }
        const form = new FormData(); form.append('video', file);
        const epTr = `${base}/video/transcribe`;
        const res = await fetch(epTr, { method: 'POST', body: form, credentials: credentialsModeForApiUrl(epTr) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { addAiReply("❌ Transcription failed: " + (data.error || res.statusText)); setAiProcessing(false); return; }
        const text = data.text || '';
        setTranscript(text);
        addAiReply(text ? `📝 Transcript:\n\n${text.slice(0, 1500)}${text.length > 1500 ? '…' : ''}` : "📝 No speech detected.");
      } catch (e) {
        addAiReply("❌ Transcription error: " + (e.message || 'Network error'));
      }
      setAiProcessing(false);
      return;
    }

    if (isDescribeFind && videoClip) {
      try {
        const file = await getVideoFileForApi(videoClip);
        if (!file) { addAiReply("❌ Please upload a video clip first."); setAiProcessing(false); return; }
        const form = new FormData(); form.append('video', file);
        const q = (command.match(/find\s+(.+?)(?:\s+in|$)/i) || command.match(/matching\s+(.+?)(?:\s+in|$)/i) || [])[1]?.trim() || 'people';
        form.append('query', q);
        const epDf = `${base}/video/describe-find`;
        const res = await fetch(epDf, { method: 'POST', body: form, credentials: credentialsModeForApiUrl(epDf) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { addAiReply("❌ Describe & Find failed: " + (data.error || res.statusText)); setAiProcessing(false); return; }
        const matches = data.matches || [];
        addAiReply(matches.length ? `🔍 Matches for "${data.query}":\n${matches.map(m => `• ${m.time.toFixed(1)}s: ${m.description || 'match'}`).join('\n')}` : `🔍 No strong matches for "${data.query}". Try another keyword.`);
      } catch (e) {
        addAiReply("❌ Describe & Find error: " + (e.message || 'Network error'));
      }
      setAiProcessing(false);
      return;
    }

    if (isColourMatch) {
      const videos = uploadedClips.filter(c => c.type === 'video');
      if (videos.length < 2) { addAiReply("❌ Colour Match needs two video clips. Add a reference and a target clip."); setAiProcessing(false); return; }
      try {
        const refFile = await getVideoFileForApi(videos[0]);
        const tgtFile = await getVideoFileForApi(videos[1]);
        if (!refFile || !tgtFile) { addAiReply("❌ Could not read clip files."); setAiProcessing(false); return; }
        const form = new FormData(); form.append('reference', refFile); form.append('target', tgtFile);
        const epCm = `${base}/video/colour-match`;
        const res = await fetch(epCm, { method: 'POST', body: form, credentials: credentialsModeForApiUrl(epCm) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { addAiReply("❌ Colour match failed: " + (data.error || res.statusText)); setAiProcessing(false); return; }
        if (data.videoUrl) {
          addRemoteVideoClip(data.videoUrl, { name: 'Colour matched', idPrefix: 'colour-match' });
          addAiReply('🎨 Colour match done — preview updated with the result. Export or keep editing.');
        } else {
          addAiReply('🎨 Colour match done. (No video URL returned.)');
        }
      } catch (e) {
        addAiReply("❌ Colour match error: " + (e.message || 'Network error'));
      }
      setAiProcessing(false);
      return;
    }

    if (isRemix) {
      const audioClip = uploadedClips.find(c => c.type === 'audio');
      if (!audioClip) { addAiReply("❌ Upload a music track first, then ask to Remix."); setAiProcessing(false); return; }
      try {
        const file = await getVideoFileForApi(audioClip);
        if (!file) { addAiReply("❌ Could not read audio file."); setAiProcessing(false); return; }
        const form = new FormData(); form.append('audio', file);
        const videoDur = duration || 60;
        form.append('targetDuration', String(videoDur));
        const epRm = `${base}/video/remix`;
        const res = await fetch(epRm, { method: 'POST', body: form, credentials: credentialsModeForApiUrl(epRm) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { addAiReply("❌ Remix failed: " + (data.error || res.statusText)); setAiProcessing(false); return; }
        addAiReply("🎵 Remix ready (duration " + videoDur + "s). Download: " + (data.audioUrl ? base + data.audioUrl : 'see response'));
      } catch (e) {
        addAiReply("❌ Remix error: " + (e.message || 'Network error'));
      }
      setAiProcessing(false);
      return;
    }

    if (isTranslate) {
      const textToTranslate = transcript || command;
      if (!textToTranslate.trim()) { addAiReply("❌ Generate a transcript first (Speech to Text), or type the text to translate."); setAiProcessing(false); return; }
      const langMatch = command.match(/translate\s+(?:to\s+)?(\w+)/i) || command.match(/(\w+)\s+language/);
      const targetLang = langMatch ? langMatch[1] : 'Spanish';
      try {
        const epTl = `${base}/video/translate`;
        const res = await fetch(epTl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: credentialsModeForApiUrl(epTl), body: JSON.stringify({ text: textToTranslate.slice(0, 4000), targetLang }) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { addAiReply("❌ Translation failed: " + (data.error || res.statusText)); setAiProcessing(false); return; }
        addAiReply(`🌐 Translation (${data.targetLang}):\n\n${(data.translated || '').slice(0, 2000)}`);
      } catch (e) {
        addAiReply("❌ Translation error: " + (e.message || 'Network error'));
      }
      setAiProcessing(false);
      return;
    }

    if (isTagAudio && videoClip) {
      try {
        const file = await getVideoFileForApi(videoClip);
        if (!file) { addAiReply("❌ Please upload a video clip first."); setAiProcessing(false); return; }
        const form = new FormData(); form.append('video', file);
        const epTa = `${base}/video/tag-audio`;
        const res = await fetch(epTa, { method: 'POST', body: form, credentials: credentialsModeForApiUrl(epTa) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { addAiReply("❌ Audio tagging failed: " + (data.error || res.statusText)); setAiProcessing(false); return; }
        const tags = data.tags || [];
        addAiReply(`🏷️ Audio tags: ${tags.join(', ')}${data.text ? '\n\nSample: ' + (data.text || '').slice(0, 200) : ''}`);
      } catch (e) {
        addAiReply("❌ Audio tagging error: " + (e.message || 'Network error'));
      }
      setAiProcessing(false);
      return;
    }

    const needsVideo = isSceneDetect || isAutoColour || isSpeechToText || isDescribeFind || isTagAudio;
    if (needsVideo && !videoClip) {
      addAiReply("❌ Please upload a video first.");
      setAiProcessing(false);
      return;
    }

    // Quick action: Add music — open file picker immediately
    if ((lowerCommand.includes('add') && lowerCommand.includes('music')) || lowerCommand === 'add romantic music') {
      addAiReply("🎵 Choose an audio file to add to the Music track (MP3, WAV, M4A).");
      setAiProcessing(false);
      setTimeout(() => musicInputRef.current?.click(), 100);
      return;
    }

    // Natural language command processing (no backend)
    await new Promise(resolve => setTimeout(resolve, 1000));
    let response = '';
    let action = null;
    if (lowerCommand.includes('cinematic') || lowerCommand.includes('cinema')) {
      response = "🎬 Applying cinematic color grade with dramatic contrast and film-like tones...";
      action = () => {
        setEffects(prev => ({
          ...prev,
          brightness: 95,
          contrast: 120,
          saturation: 110,
          temperature: 5,
        }));
      };
    } else if (lowerCommand.includes('romantic') || lowerCommand.includes('soft')) {
      response = "💕 Creating romantic atmosphere with warm, soft tones and gentle transitions...";
      action = () => {
        setEffects(prev => ({
          ...prev,
          brightness: 105,
          contrast: 95,
          saturation: 115,
          temperature: 15,
        }));
      };
    } else if (lowerCommand.includes('slow motion') || lowerCommand.includes('slow mo')) {
      response = "⏱️ Applying slow motion effect. Which clip would you like to slow down?";
      action = () => {
        setEffects(prev => ({ ...prev, slowMotion: true }));
      };
    } else if (lowerCommand.includes('stabilize') || lowerCommand.includes('shaky')) {
      response = "✨ Applying AI video stabilization to remove camera shake...";
      action = () => {
        setEffects(prev => ({ ...prev, stabilization: true }));
      };
    } else if (lowerCommand.includes('sync') || lowerCommand.includes('multi') || lowerCommand.includes('camera')) {
      response = "🎥 Syncing multiple camera angles automatically using audio waveforms...";
      action = () => {
        setMultiCamMode(true);
        setSyncedCameras(['Main Camera', 'Second Camera', 'Drone']);
      };
    } else if (lowerCommand.includes('enhance speech') || lowerCommand.includes('enhance dialogue') || (lowerCommand.includes('speech') && lowerCommand.includes('enhance'))) {
      response = "🎤 Emphasizing dialogue and reducing background level for preview...";
      action = () => {
        setAudioVolumes(prev => {
          const next = { ...prev };
          tracks.filter(t => t.type === 'audio' && t.name?.toLowerCase().includes('dialogue')).forEach(t => {
            t.clips.forEach(c => { if (c.id) next[c.id] = Math.min(1, (next[c.id] ?? 0.7) + 0.2); });
          });
          return next;
        });
      };
    } else if (lowerCommand.includes('reframe') || (lowerCommand.includes('9:16') && lowerCommand.includes('social'))) {
      response = "📱 For 9:16 vertical (Reels/TikTok), export your video then reframe in a desktop editor or social app. Here we’ve noted your preference.";
      action = () => { /* optional: set export aspect when UI supports it */ };
    } else if (lowerCommand.includes('highlight') || lowerCommand.includes('best moment')) {
      response = "🌟 Analyzing footage for best moments... Found:\n• First Kiss (0:45)\n• First Dance (1:32)\n• Emotional Speech (2:15)\n\nCreating 2-minute highlight reel...";
      action = () => {
        // Create highlight markers
        setMarkers([
          { id: 'm1', time: 45, label: 'First Kiss', type: 'highlight' },
          { id: 'm2', time: 92, label: 'First Dance', type: 'highlight' },
          { id: 'm3', time: 135, label: 'Speech', type: 'highlight' },
        ]);
      };
    } else if (lowerCommand.includes('music') || lowerCommand.includes('audio')) {
      if (lowerCommand.includes('quiet') || lowerCommand.includes('lower')) {
        response = "🔊 Lowering music volume during dialogue. Applying audio ducking...";
      } else if (lowerCommand.includes('loud') || lowerCommand.includes('boost')) {
        response = "🔊 Boosting audio levels and reducing background noise...";
      } else {
        response = "🎵 I can help with audio! Try:\n• 'Lower music during speeches'\n• 'Boost dialogue audio'\n• Or click 🎵 Music to import a track.";
      }
    } else if (lowerCommand.includes('title') || lowerCommand.includes('text')) {
      response = "📝 What text would you like to add? I can create beautiful wedding titles with:\n• Couple names\n• Date & location\n• Custom messages";
    } else if (lowerCommand.includes('export') || lowerCommand.includes('render')) {
      response = "💾 Ready to export! What quality?\n• 4K (3840x2160) - Best quality\n• 1080p (1920x1080) - Recommended\n• 720p (1280x720) - Fast upload";
    } else if (lowerCommand.includes('undo') || lowerCommand.includes('go back')) {
      response = "↶ Undoing last change...";
    } else if (lowerCommand.includes('help') || lowerCommand.includes('what can')) {
      response = "I can help you with:\n\n✨ Editing:\n• 'Make it cinematic'\n• 'Apply slow motion'\n• 'Stabilize shaky clips'\n\n🎥 Organization:\n• 'Sync all cameras'\n• 'Find best moments'\n• 'Create highlight reel'\n\n🎨 Color:\n• 'Make it romantic'\n• 'Apply vintage look'\n• 'Enhance skin tones'\n\n🔊 Audio:\n• 'Lower music during speeches'\n• 'Boost dialogue'\n• 'Add background music'\n\nJust tell me what you want!";
    } else {
      response = "I understand you want to edit your video. Could you be more specific? For example:\n• 'Make the ceremony more cinematic'\n• 'Add slow motion to the kiss'\n• 'Create a 2-minute highlight reel'\n\nType 'help' to see what I can do!";
    }

    // Add AI response
    const aiMsg = { id: Date.now() + 1, sender: 'ai', text: response };
    setAiMessages(prev => [...prev, aiMsg]);

    // Execute action if any
    if (action) {
      setTimeout(() => {
        action();
        const doneMsg = { id: Date.now() + 2, sender: 'ai', text: "✅ Done! Check the preview." };
        setAiMessages(prev => [...prev, doneMsg]);
      }, 1500);
    }

    } catch (err) {
      addAiReply("❌ Something went wrong: " + (err?.message || String(err)));
    } finally {
      setAiProcessing(false);
    }

    // Scroll to bottom
    setTimeout(() => {
      if (aiChatRef.current) {
        aiChatRef.current.scrollTop = aiChatRef.current.scrollHeight;
      }
    }, 100);
  };

  const handleAISubmit = (e) => {
    e.preventDefault();
    if (aiInput.trim() && !aiProcessing) {
      processAICommand(aiInput);
      setAiInput('');
    }
  };

  // File upload (used by both input and drag-and-drop)
  const addFilesAsClips = (files, targetTrackType = null) => {
    const list = Array.isArray(files) ? files : Array.from(files || []);
    const valid = list.filter(f => f && (f.type?.startsWith('video/') || f.type?.startsWith('audio/')));
    if (valid.length === 0) return;
    const newClips = valid.map((file, idx) => ({
      id: `clip-${Date.now()}-${idx}`,
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      type: file.type.startsWith('video') ? 'video' : 'audio',
      duration: 0,
      thumbnail: null,
    }));
    setUploadedClips(prev => [...prev, ...newClips]);

    // Auto-add to appropriate track if specified
    if (targetTrackType) {
      newClips.forEach(clip => {
        const targetTrack = tracks.find(t => t.type === clip.type && !t.locked);
        if (targetTrack) {
          addClipToTrack(clip, targetTrack.id);
        }
      });
    }

    const aiMsg = {
      id: Date.now(),
      sender: 'ai',
      text: `📁 Imported ${valid.length} file(s). ${newClips.filter(c => c.type === 'audio').length > 0 ? 'Audio tracks added to timeline.' : ''} Would you like me to:\n• Sync multiple camera angles?\n• Detect key moments?\n• Create a highlight reel?`
    };
    setAiMessages(prev => [...prev, aiMsg]);
  };

  const handleFileUpload = (e) => {
    addFilesAsClips(e.target.files || []);
    e.target.value = '';
  };

  const handleMusicUpload = (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type?.startsWith('audio/'));
    if (files.length === 0) {
      alert('Please select audio files (.mp3, .wav, .m4a, etc.)');
      return;
    }
    addFilesAsClips(files, 'audio');
    e.target.value = '';
  };

  const [dragOver, setDragOver] = useState(false);
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    addFilesAsClips(e.dataTransfer?.files || []);
  };

  // Add clip to track (functional update to avoid stale state)
  const addClipToTrack = (clip, trackId) => {
    pushUndoState();
    setTracks(prev => prev.map(track => {
      if (track.id === trackId && track.type === clip.type) {
        const dur = clip.duration || 10;
        const startTime = track.clips.reduce((max, c) => Math.max(max, (c.startTime ?? 0) + (c.clipDuration ?? c.duration ?? 0)), 0);
        const newClip = {
          ...clip,
          trackId,
          startTime,
          clipDuration: dur,
          sourceStart: clip.sourceStart ?? 0,
          sourceEnd: clip.sourceEnd ?? dur,
        };
        return { ...track, clips: [...track.clips, newClip] };
      }
      return track;
    }));
  };

  const splitClipAtPlayhead = () => {
    if (!selectedClip) return;
    pushUndoState();
    const t = currentTime;
    const clipStart = selectedClip.startTime;
    const clipEnd = clipStart + (selectedClip.clipDuration ?? selectedClip.duration ?? 0);
    if (t <= clipStart || t >= clipEnd) return;
    const leftDuration = t - clipStart;
    const rightDuration = clipEnd - t;
    if (leftDuration < 0.1 || rightDuration < 0.1) return;
    setTracks(prev => prev.map(track => {
      const idx = track.clips.findIndex(c => c.id === selectedClip.id);
      if (idx === -1) return track;
      const clip = track.clips[idx];
      const srcStart = clip.sourceStart ?? 0;
      const srcEnd = clip.sourceEnd ?? (clip.clipDuration ?? clip.duration ?? 10);
      const left = { ...clip, clipDuration: leftDuration, sourceStart: srcStart, sourceEnd: srcStart + leftDuration };
      const right = { ...clip, id: `${clip.id}-split-${Date.now()}`, startTime: t, clipDuration: rightDuration, sourceStart: srcStart + leftDuration, sourceEnd: srcEnd };
      const newClips = [...track.clips.slice(0, idx), left, right, ...track.clips.slice(idx + 1)];
      return { ...track, clips: newClips };
    }));
    setSelectedClip(null);
  };

  const deleteSelectedClip = () => {
    if (!selectedClip) return;
    pushUndoState();
    setTracks(prev => prev.map(track => ({
      ...track,
      clips: track.clips.filter(c => c.id !== selectedClip.id),
    })));
    setSelectedClip(null);
  };

  const handleClipDragStart = (e, clip, trackId) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    pushUndoState();
    const startTime = clip.startTime ?? 0;
    const clipDur = clip.clipDuration ?? clip.duration ?? 10;
    clipDragRef.current = { clipId: clip.id, trackId, startTime, startX: e.clientX, clipDur };
    const onMove = (e2) => {
      if (!clipDragRef.current) return;
      const { clipId, trackId: tid, startTime: st, startX, clipDur: dur } = clipDragRef.current;
      const deltaX = e2.clientX - startX;
      const deltaTime = deltaX / (zoom * 10);
      let newStart = st + deltaTime;
      newStart = Math.max(0, Math.min(duration - dur, newStart));
      newStart = Math.round(newStart / SNAP_GRID_SEC) * SNAP_GRID_SEC;
      setTracks(prev => prev.map(t => {
        if (t.id !== tid) return t;
        return { ...t, clips: t.clips.map(c => c.id === clipId ? { ...c, startTime: newStart } : c) };
      }));
      clipDragRef.current.startX = e2.clientX;
      clipDragRef.current.startTime = newStart;
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      clipDragRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const fitTimelineToContent = useCallback(() => {
    let maxEnd = duration;
    tracks.forEach(track => {
      track.clips.forEach(clip => {
        const end = (clip.startTime ?? 0) + (clip.clipDuration ?? clip.duration ?? 0);
        maxEnd = Math.max(maxEnd, end);
      });
    });
    const totalTime = Math.max(maxEnd, 1);
    const w = timelineTrackContentRef.current?.clientWidth ?? 500;
    const newZoom = w / (totalTime * 10);
    setZoom(Math.min(5, Math.max(0.5, newZoom)));
  }, [duration, tracks]);

  // Get ordered video clips from timeline (all video tracks, sorted by startTime)
  const getTimelineVideoClips = useCallback(() => {
    const videoTracks = tracks.filter(t => t.type === 'video');
    const all = [];
    videoTracks.forEach(t => t.clips.forEach(c => all.push({ ...c, trackId: t.id })));
    all.sort((a, b) => (a.startTime ?? 0) - (b.startTime ?? 0));
    return all;
  }, [tracks]);

  // Export: multi-clip with transitions (POST /video/render) or single-clip full options (POST /video/pro-export)
  const handleExport = async () => {
    const timelineClips = getTimelineVideoClips();
    const apiBaseUrl = apiBase();

    if (timelineClips.length === 0) {
      const fallback = uploadedClips.find(c => c.type === 'video');
      if (!fallback) {
        alert('Please import video footage first.');
        return;
      }
    }

    setProcessing(true);
    setRenderProgress(10);

    const startMsg = { id: Date.now(), sender: 'ai', text: timelineClips.length > 1 ? '🎬 Rendering multi-clip sequence with transitions…' : '🎬 Starting real export (trim, color, speed, fades, titles)…' };
    setAiMessages(prev => [...prev, startMsg]);

    const resolveFile = async (clip) => {
      const uploaded = uploadedClips.find(u => u.id === clip.id || u.id === clip.id?.replace(/-split-\d+$/, '').replace(/-copy-\d+$/, ''));
      if (!uploaded) return null;
      if (uploaded.file) return uploaded.file;
      if (uploaded.url) {
        const res = await fetch(uploaded.url);
        return await res.blob();
      }
      return null;
    };

    if (timelineClips.length >= 2) {
      const files = [];
      for (const clip of timelineClips) {
        const file = await resolveFile(clip);
        if (!file) {
          setProcessing(false);
    setRenderProgress(0);
          setAiMessages(prev => [...prev, { id: Date.now(), sender: 'ai', text: `❌ Clip "${clip.name || clip.id}" has no source file. Re-import and add to timeline.` }]);
          return;
        }
        files.push(file);
      }
      setRenderProgress(30);
      const formData = new FormData();
      files.forEach((f, i) => formData.append('clips', f, `clip-${i}.mp4`));
      const project = {
        clips: timelineClips.map((c, i) => ({ index: i, start: c.sourceStart ?? 0, end: c.sourceEnd ?? (c.clipDuration ?? c.duration ?? null), speed: 1 })),
        transitions: timelineClips.length > 1 ? Array.from({ length: timelineClips.length - 1 }, () => ({ type: transitionType, duration: 0.5 })) : [],
      };
      formData.append('project', JSON.stringify(project));
      try {
        const epR = `${apiBaseUrl}/video/render`;
        const res = await fetch(epR, { method: 'POST', body: formData, credentials: credentialsModeForApiUrl(epR) });
        const data = await res.json().catch(() => ({}));
        setRenderProgress(100);
        setProcessing(false);
        if (res.ok && data.videoUrl) {
          const downloadUrl = data.videoUrl.startsWith('http') ? data.videoUrl : apiBaseUrl + data.videoUrl;
          setAiMessages(prev => [...prev, { id: Date.now(), sender: 'ai', text: `✅ Multi-clip export complete (${timelineClips.length} clips, ${transitionType} transitions).` }]);
          const a = document.createElement('a');
          a.href = downloadUrl;
          a.download = `export-${Date.now()}.mp4`;
          a.target = '_blank';
          a.click();
        } else {
          setAiMessages(prev => [...prev, { id: Date.now(), sender: 'ai', text: `❌ Render failed: ${data.error || res.statusText}.` }]);
        }
      } catch (err) {
        setRenderProgress(0);
        setProcessing(false);
        setAiMessages(prev => [...prev, { id: Date.now(), sender: 'ai', text: `❌ ${err.message}.` }]);
      }
      return;
    }

    const videoClip = (timelineClips[0] && uploadedClips.find(u => u.id === timelineClips[0].id || u.id === String(timelineClips[0].id).replace(/-split-\d+$/, ''))) || uploadedClips.find(c => c.type === 'video');
    if (!videoClip) {
          setProcessing(false);
      setRenderProgress(0);
      setAiMessages(prev => [...prev, { id: Date.now(), sender: 'ai', text: '❌ No video to export.' }]);
      return;
    }

    let fileToSend = videoClip.file;
    if (!fileToSend && videoClip.url) {
      try {
        const res = await fetch(videoClip.url);
        fileToSend = await res.blob();
      } catch (e) {
        setProcessing(false);
        setRenderProgress(0);
        setAiMessages(prev => [...prev, { id: Date.now(), sender: 'ai', text: `❌ Could not read video: ${e.message}.` }]);
        return;
      }
    }
    if (!fileToSend) {
      setProcessing(false);
      setRenderProgress(0);
      setAiMessages(prev => [...prev, { id: Date.now(), sender: 'ai', text: '❌ No video file. Re-import and try again.' }]);
      return;
    }

    const project = {
      trim: { start: 0, end: duration },
      brightness: effects.brightness,
      contrast: effects.contrast,
      saturation: effects.saturation,
      speed: exportSpeed,
      reverse: exportReverse,
      fadeIn: fadeInSec,
      fadeOut: fadeOutSec,
      textOverlays: titleOverlays.map((t) => ({
        text: t.text,
        position: t.position || 'bottom',
        startTime: t.startTime ?? 0,
        endTime: t.endTime ?? duration,
        fontSize: t.fontSize ?? 24,
      })),
      stabilize: exportStabilize,
      format: exportFormat,
      quality: exportQuality,
    };

    setRenderProgress(30);
    const formData = new FormData();
    formData.append('video', fileToSend, videoClip.name || 'video.mp4');
    formData.append('project', JSON.stringify(project));

    try {
      const epP = `${apiBaseUrl}/video/pro-export`;
      const res = await fetch(epP, { method: 'POST', body: formData, credentials: credentialsModeForApiUrl(epP) });
      const data = await res.json().catch(() => ({}));
      setRenderProgress(100);
      setProcessing(false);

      if (res.ok && data.success && data.videoUrl) {
        const downloadUrl = data.downloadUrl || (data.videoUrl.startsWith('http') ? data.videoUrl : apiBaseUrl + (data.videoUrl.startsWith('/') ? data.videoUrl : '/' + data.videoUrl));
        setAiMessages(prev => [...prev, {
            id: Date.now(),
            sender: 'ai',
          text: `✅ Export complete (${exportFormat.toUpperCase()}, ${exportQuality}, ${exportStabilize ? 'stabilized, ' : ''}trim + color + speed + fades + titles).`
        }]);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = data.filename || `export-${Date.now()}.${exportFormat}`;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
      } else {
        setAiMessages(prev => [...prev, { id: Date.now(), sender: 'ai', text: `❌ Export failed: ${data.error || res.statusText}.` }]);
        const a = document.createElement('a');
        a.href = videoClip.url;
        a.download = videoClip.name || 'original.mp4';
        a.click();
      }
    } catch (err) {
      setRenderProgress(0);
      setProcessing(false);
      setAiMessages(prev => [...prev, { id: Date.now(), sender: 'ai', text: `❌ ${err.message}.` }]);
      const a = document.createElement('a');
      a.href = videoClip.url;
      a.download = videoClip.name || 'original.mp4';
      a.click();
    }
  };

  // Playback — sync with actual video element so play/pause and seek work
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    setPlaying((prev) => {
      if (prev) {
        v.pause();
        return false;
      }
      v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      return prev;
    });
  }, []);

  /** Seek to time (seconds); updates both state and video element. Use for scrubber, markers, skip. */
  const seekTo = useCallback((t) => {
    const safeDuration = Number.isFinite(duration) ? duration : 0;
    const sec = Math.max(0, Math.min(Number(t), safeDuration));
    if (!Number.isFinite(sec)) return;
    setCurrentTime(sec);
    if (videoRef.current) videoRef.current.currentTime = sec;
  }, [duration]);

  const skipForward = useCallback(() => {
    seekTo(Math.min(currentTime + 5, duration));
  }, [seekTo, currentTime, duration]);

  const skipBackward = useCallback(() => {
    seekTo(Math.max(currentTime - 5, 0));
  }, [seekTo, currentTime]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = playbackSpeed;
    // Also update audio playback speed
    audioRefs.current.forEach(audio => {
      if (audio) audio.playbackRate = playbackSpeed;
    });
  }, [playbackSpeed, uploadedClips.length]);

  const currentTimeRef = useRef(currentTime);
  currentTimeRef.current = currentTime;
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedClip(null);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
        return;
      }
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        togglePlay();
        return;
      }
      if (e.key === 'ArrowLeft' || (e.key === 'j' && !e.ctrlKey && !e.metaKey && !e.altKey)) {
        e.preventDefault();
        skipBackward();
        return;
      }
      if (e.key === 'ArrowRight' || (e.key === 'l' && !e.ctrlKey && !e.metaKey && !e.altKey)) {
        e.preventDefault();
        skipForward();
        return;
      }
      if (e.key === 'k' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        togglePlay();
        return;
      }
      if (e.key.toLowerCase() === 'm' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setMarkers(prev => [...prev, { id: `marker-${Date.now()}`, time: currentTimeRef.current, label: `Marker ${prev.length + 1}` }]);
        return;
      }
      if (e.key.toLowerCase() === 's' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const sel = selectedClip;
        if (!sel) return;
        const start = sel.startTime ?? 0;
        const end = start + (sel.clipDuration ?? sel.duration ?? 0);
        const t = currentTimeRef.current;
        if (t > start && t < end) {
          e.preventDefault();
          setTracks(prev => prev.map(track => {
            const idx = track.clips.findIndex(c => c.id === sel.id);
            if (idx === -1) return track;
            const clip = track.clips[idx];
            const dur = clip.clipDuration ?? clip.duration ?? 10;
            const leftDuration = t - (clip.startTime ?? 0);
            const rightDuration = dur - leftDuration;
            if (leftDuration < 0.1 || rightDuration < 0.1) return track;
            const left = { ...clip, clipDuration: leftDuration };
            const right = { ...clip, id: `${clip.id}-split-${Date.now()}`, startTime: t, clipDuration: rightDuration };
            const newClips = [...track.clips.slice(0, idx), left, right, ...track.clips.slice(idx + 1)];
            return { ...track, clips: newClips };
          }));
          setSelectedClip(null);
        }
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (selectedClip) {
          e.preventDefault();
          setTracks(prev => prev.map(track => ({ ...track, clips: track.clips.filter(c => c.id !== selectedClip.id) })));
          setSelectedClip(null);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, selectedClip, togglePlay, skipForward, skipBackward, undo, redo]);

  if (!isOpen) return null;

  const hasVideoPreview = uploadedClips.some((c) => c.type === 'video');

  return (
    <div
      ref={containerRef}
      className={`pro-video-editor-v2${isEmbedded ? ' pro-video-editor-v2-embedded' : ''}${isFullscreen ? ' pro-video-editor-v2-fullscreen' : ''}`}
    >
      {isEmbedded && (
        <div className="editor-embedded-bar">
          {onSwitchToImageEditor && (
            <button type="button" className="editor-header-switch-btn" onClick={onSwitchToImageEditor} title="Switch to Image Editor" aria-label="Switch to Image Editor">🖼️ Image</button>
          )}
          {onSwitchToChat && (
            <button type="button" className="editor-header-switch-btn" onClick={onSwitchToChat} title="Back to Chat" aria-label="Back to Chat">💬 Chat</button>
          )}
          <button type="button" className="editor-fullscreen-btn" onClick={toggleFullscreen} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
          </button>
        </div>
      )}
      {!isEmbedded && (
        <div className="editor-header">
          <div className="editor-header-brand">
            <span className="editor-header-logo">🎬</span>
            <div>
              <h1 className="editor-header-title">Zolaid Pro Video Editor</h1>
              <p className="editor-header-subtitle">Timeline • Multi-cam • AI Assistant • Export</p>
            </div>
          </div>
          <div className="header-tools">
            <span className="feature-badge">AI</span>
            <span className="feature-badge">Multi-cam</span>
            <span className="feature-badge">4K</span>
            <button type="button" onClick={toggleFullscreen} className="editor-fullscreen-btn" title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
            </button>
            {onSwitchToImageEditor && (
              <button type="button" className="editor-header-switch-btn" onClick={onSwitchToImageEditor} title="Switch to Image Editor" aria-label="Switch to Image Editor">🖼️ Image</button>
            )}
            {onSwitchToChat && (
              <button type="button" className="editor-header-switch-btn" onClick={onSwitchToChat} title="Back to Chat" aria-label="Back to Chat">💬 Chat</button>
            )}
            <button type="button" onClick={onClose} className="close-btn" title="Close" aria-label="Close editor">✕</button>
          </div>
        </div>
      )}

      <div className="editor-layout">
        {/* Left Sidebar - collapsible; when closed: only + for minimal view (preview + typing bar) */}
        <div className={`sidebar-left ${leftSidebarOpen ? 'sidebar-left-open' : 'sidebar-left-collapsed'}`}>
          <button
            type="button"
            className="sidebar-left-toggle"
            onClick={() => setLeftSidebarOpen(!leftSidebarOpen)}
            title={leftSidebarOpen ? 'Hide sidebar (minimal view)' : 'Show media & options'}
            aria-label={leftSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {leftSidebarOpen ? '◀' : '+'}
          </button>
          {leftSidebarOpen && (
          <div className="sidebar-left-content">
          <div className="section">
            <h3>📁 Media Library</h3>
            <div
              className={`import-footage-zone ${dragOver ? 'import-footage-zone-active' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept="video/*"
                multiple
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                id="video-upload"
              />
              <input
                ref={musicInputRef}
                type="file"
                accept="audio/*"
                multiple
                onChange={handleMusicUpload}
                style={{ display: 'none' }}
                id="music-upload"
              />
              <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                <label htmlFor="video-upload" className="upload-btn import-footage-btn" style={{flex: 1, minWidth: '120px'}}>
                  🎥 Import Video
              </label>
                <label htmlFor="music-upload" className="upload-btn import-footage-btn" style={{flex: 1, minWidth: '120px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
                  🎵 Import Music
                </label>
              </div>
              <p className="import-footage-hint">Video & music • click or drag</p>
            </div>
            <div className="media-grid">
              {uploadedClips.map(clip => (
                <div
                  key={clip.id}
                  className="media-item"
                  draggable={clip.type !== 'audio'}
                  onDragStart={(e) => {
                    if (clip.type === 'audio') {
                      e.preventDefault();
                      return;
                    }
                    e.dataTransfer.setData('clip', JSON.stringify(clip));
                  }}
                  style={{
                    border: clip.type === 'audio' ? '2px solid #667eea' : '2px solid #4CAF50',
                    cursor: clip.type === 'audio' ? 'default' : 'grab'
                  }}
                >
                  <div className="media-thumbnail" style={{
                    background: clip.type === 'audio' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)'
                  }}>
                    {clip.type === 'video' ? '🎥' : '🎵'}
                  </div>
                  <span className="media-name" title={clip.name}>{clip.name}</span>
                  <span style={{fontSize: '10px', color: '#888', marginTop: '4px'}}>
                    {clip.type === 'video' ? 'Video' : 'Audio'}
                  </span>
                  {clip.type === 'audio' && (
                    <div style={{marginTop: '8px', width: '100%'}}>
                      <label style={{fontSize: '10px', display: 'flex', alignItems: 'center', gap: '4px'}}>
                        🔊 {Math.round((audioVolumes[clip.id] ?? 0.7) * 100)}%
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={(audioVolumes[clip.id] ?? 0.7) * 100}
                          onChange={(e) => {
                            const vol = Number(e.target.value) / 100;
                            setAudioVolumes(prev => ({...prev, [clip.id]: vol}));
                            // Update audio element volume immediately
                            const audioIdx = uploadedClips.filter(c => c.type === 'audio').indexOf(clip);
                            if (audioRefs.current[audioIdx]) {
                              audioRefs.current[audioIdx].volume = vol;
                            }
                          }}
                          style={{flex: 1, minWidth: 0}}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* What's new in AI-powered editing */}
          <div className="section section-ai-features">
            <h3>What&apos;s new in AI-powered editing</h3>
            <p className="ai-features-intro">
              Unlock smart features to edit faster and push your creativity further.
            </p>
            <div className="ai-features-grid">
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Use Object Mask to mask and track the subject in this clip')} title="Hover over subject, click — AI masks and tracks it">
                🎭 Object Mask
              </button>
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Generative Extend: extend this clip for a smoother cut')} title="Extend clips with AI — add frames, lengthen sound">
                ↗️ Generative Extend
              </button>
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Search my footage: find clips matching my description')} title="Describe what you need — find matching clips in seconds">
                🔍 Describe & Find
              </button>
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Run Scene Edit Detection and add cuts at each scene change')} title="Auto-detect scene transitions and cut or add markers">
                ✂️ Scene Edit Detection
              </button>
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Apply Auto Colour: fix exposure, contrast and white balance')} title="One-click colour correction">
                🎨 Auto Colour
              </button>
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Enhance Speech: remove background noise and improve dialogue')} title="AI dialogue cleanup">
                🎤 Enhance Speech
              </button>
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Generate transcript and captions with Speech to Text')} title="Auto captions and transcripts in 18+ languages">
                📝 Speech to Text
              </button>
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Text-Based Editing: highlight text to add clips to timeline')} title="Edit by editing the transcript">
                📄 Text-Based Edit
              </button>
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Auto Reframe this for 9:16 vertical for social')} title="Reframe for Instagram/TikTok keeping subject in frame">
                📱 Auto Reframe
              </button>
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Auto Ducking: lower music when dialogue plays')} title="Duck music under dialogue automatically">
                🔊 Auto Ducking
              </button>
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Colour Match: match the look of this clip to the previous one')} title="Match colours between two clips">
                🎚️ Colour Match
              </button>
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Apply Morph Cut to smooth this talking-head transition')} title="Smooth interview cuts with face tracking">
                🔀 Morph Cut
              </button>
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Remix: retime music to match my video length')} title="Sync music length to video with AI">
                🎵 Remix
              </button>
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Auto-translate captions to multiple languages')} title="Translate captions into 27 languages">
                🌐 Caption Translate
              </button>
              <button type="button" className="ai-feature-btn" disabled={aiProcessing} onClick={() => processAICommand('Tag audio clips: music, dialogue, SFX, ambiance')} title="AI audio category badges">
                🏷️ AI Audio Tagging
              </button>
            </div>
          </div>

          {/* Quick Actions & Export (moved from right sidebar so right is AI-only) */}
          <div className="section quick-actions-section">
            <h3>⚡ Quick Actions</h3>
            <div className="quick-actions-grid">
              <button type="button" onClick={() => processAICommand('Make it cinematic')} className="quick-btn" disabled={aiProcessing} title="Apply cinematic look">🎬 Cinematic</button>
              <button type="button" onClick={() => processAICommand('Create highlight reel')} className="quick-btn" disabled={aiProcessing} title="Create highlight reel">✨ Highlight Reel</button>
              <button type="button" onClick={() => processAICommand('Sync all cameras')} className="quick-btn" disabled={aiProcessing} title="Sync multi-cam">🎥 Sync</button>
              <button type="button" onClick={() => processAICommand('Add romantic music')} className="quick-btn" disabled={aiProcessing} title="Add music">🎵 Music</button>
              <button type="button" onClick={() => processAICommand('Run Scene Edit Detection on the timeline')} className="quick-btn" disabled={aiProcessing} title="Scene detection">✂️ Scenes</button>
              <button type="button" onClick={() => processAICommand('Apply Auto Colour to selected clip')} className="quick-btn" disabled={aiProcessing} title="Auto colour">🎨 Colour</button>
              <button type="button" onClick={() => processAICommand('Enhance Speech on dialogue')} className="quick-btn" disabled={aiProcessing} title="Enhance speech">🎤 Speech</button>
              <button type="button" onClick={() => processAICommand('Generate captions with Speech to Text')} className="quick-btn" disabled={aiProcessing} title="Generate captions">📝 Captions</button>
              <button type="button" onClick={() => processAICommand('Describe and find clips in my media')} className="quick-btn" disabled={aiProcessing} title="Find clips">🔍 Find</button>
              <button type="button" onClick={() => processAICommand('Auto Reframe for 9:16 social')} className="quick-btn" disabled={aiProcessing} title="Reframe 9:16">📱 Reframe</button>
            </div>
          </div>
          <div className="section">
            <h3>🎨 Effects Controls</h3>
            <div className="quick-actions-grid">
              <button
                type="button"
                onClick={() => setEffects(prev => ({ ...prev, brightness: Math.min(200, prev.brightness + 10) }))}
                className="quick-btn"
                style={{background: '#4CAF50'}}
                disabled={aiProcessing}
                title="Brighter"
              >
                ☀️ Brighter
              </button>
              <button
                type="button"
                onClick={() => setEffects(prev => ({ ...prev, contrast: Math.min(200, prev.contrast + 10) }))}
                className="quick-btn"
                style={{background: '#2196F3'}}
                disabled={aiProcessing}
                title="Contrast"
              >
                📊 Contrast
              </button>
              <button
                type="button"
                onClick={() => setEffects(prev => ({ ...prev, saturation: Math.min(200, prev.saturation + 10) }))}
                className="quick-btn"
                style={{background: '#FF9800'}}
                disabled={aiProcessing}
                title="Saturate"
              >
                🌈 Saturate
              </button>
              <button
                type="button"
                onClick={() => {
                  setPlaybackSpeed(0.5);
                  if (videoRef.current) videoRef.current.playbackRate = 0.5;
                  audioRefs.current.forEach(a => { if (a) a.playbackRate = 0.5; });
                }}
                className="quick-btn"
                style={{background: '#9C27B0'}}
                disabled={!uploadedClips.some(c => c.type === 'video')}
                title="0.5x speed"
              >
                🐌 0.5x Speed
              </button>
              <button
                type="button"
                onClick={() => {
                  setPlaybackSpeed(1);
                  if (videoRef.current) videoRef.current.playbackRate = 1;
                  audioRefs.current.forEach(a => { if (a) a.playbackRate = 1; });
                }}
                className="quick-btn"
                style={{background: '#607D8B'}}
                disabled={!uploadedClips.some(c => c.type === 'video')}
                title="1x speed"
              >
                ▶️ 1x Speed
              </button>
              <button
                type="button"
                onClick={() => {
                  setPlaybackSpeed(2);
                  if (videoRef.current) videoRef.current.playbackRate = 2;
                  audioRefs.current.forEach(a => { if (a) a.playbackRate = 2; });
                }}
                className="quick-btn"
                style={{background: '#795548'}}
                disabled={!uploadedClips.some(c => c.type === 'video')}
                title="2x speed"
              >
                🚀 2x Speed
              </button>
              <button
                type="button"
                onClick={() => {
                  setEffects({
                    brightness: 100,
                    contrast: 100,
                    saturation: 100,
                    temperature: 0,
                    stabilization: false,
                    slowMotion: false,
                    speedRamp: false,
                  });
                  setPlaybackSpeed(1);
                  setExportSpeed(1);
                  setExportReverse(false);
                  setFadeInSec(0);
                  setFadeOutSec(0);
                  setExportStabilize(false);
                  setExportFormat('mp4');
                  setExportQuality('high');
                  if (videoRef.current) videoRef.current.playbackRate = 1;
                  audioRefs.current.forEach(audio => { if (audio) audio.playbackRate = 1; });
                }}
                className="quick-btn"
                style={{background: '#f44336'}}
                disabled={aiProcessing}
                title="Reset effects and export options"
              >
                ↺ Reset All
              </button>
            </div>
            <h3 style={{marginTop: '12px'}}>🎞️ Export options (baked in)</h3>
            <div className="export-options-block">
              <div className="export-options-row">
                <label className="export-option">
                  <span className="export-option-label">Speed</span>
                  <select value={exportSpeed} onChange={(e) => setExportSpeed(Number(e.target.value))} className="export-option-input">
                    <option value={0.25}>0.25×</option>
                    <option value={0.5}>0.5×</option>
                    <option value={1}>1×</option>
                    <option value={1.5}>1.5×</option>
                    <option value={2}>2×</option>
                  </select>
                </label>
                <label className="export-option export-option-check">
                  <input type="checkbox" checked={exportReverse} onChange={(e) => setExportReverse(e.target.checked)} />
                  <span>Reverse</span>
                </label>
                <label className="export-option">
                  <span className="export-option-label">Format</span>
                  <select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)} className="export-option-input">
                    <option value="mp4">MP4</option>
                    <option value="webm">WebM</option>
                  </select>
                </label>
                <label className="export-option">
                  <span className="export-option-label">Quality</span>
                  <select value={exportQuality} onChange={(e) => setExportQuality(e.target.value)} className="export-option-input">
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </label>
              </div>
              <div className="export-options-row">
                <label className="export-option">
                  <span className="export-option-label">Fade in (s)</span>
                  <input type="number" min={0} max={10} step={0.5} value={fadeInSec} onChange={(e) => setFadeInSec(Number(e.target.value) || 0)} className="export-option-input export-option-number" />
                </label>
                <label className="export-option">
                  <span className="export-option-label">Fade out (s)</span>
                  <input type="number" min={0} max={10} step={0.5} value={fadeOutSec} onChange={(e) => setFadeOutSec(Number(e.target.value) || 0)} className="export-option-input export-option-number" />
                </label>
                <label className="export-option export-option-check">
                  <input type="checkbox" checked={exportStabilize} onChange={(e) => setExportStabilize(e.target.checked)} />
                  <span>Stabilize</span>
                </label>
                {getTimelineVideoClips().length >= 2 && (
                  <label className="export-option">
                    <span className="export-option-label">Transition</span>
                    <select value={transitionType} onChange={(e) => setTransitionType(e.target.value)} className="export-option-input">
                      <option value="fade">Fade</option>
                      <option value="dissolve">Dissolve</option>
                      <option value="wipeleft">Wipe Left</option>
                      <option value="wiperight">Wipe Right</option>
                      <option value="slideleft">Slide Left</option>
                      <option value="slideright">Slide Right</option>
                    </select>
                  </label>
                )}
              </div>
              <div className="export-options-row">
                <button type="button" className="quick-btn export-add-title-btn" onClick={() => setTitleOverlays(prev => [...prev, { id: Date.now(), text: 'Title', position: 'bottom', startTime: 0, endTime: duration || 60, fontSize: 24 }])} style={{background: '#3F51B5'}}>
                  ➕ Add title
                </button>
              </div>
              {titleOverlays.length > 0 && (
                <ul className="export-titles-list">
                  {titleOverlays.map((t, i) => (
                    <li key={t.id || i}>
                      &quot;{t.text}&quot; <button type="button" onClick={() => setTitleOverlays(prev => prev.filter((_, j) => j !== i))} className="export-titles-remove">Remove</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="export-current-values">
              <span className="export-current-label">Preview:</span>
              <span>Brightness {effects.brightness}%</span>
              <span>Contrast {effects.contrast}%</span>
              <span>Saturation {effects.saturation}%</span>
              <span>Speed {playbackSpeed}×</span>
            </div>
          </div>
          <div className="section export-section-left">
            <h3>📤 Export</h3>
            <button
              type="button"
              className="export-btn"
              onClick={handleExport}
              disabled={processing || !uploadedClips.some(c => c.type === 'video')}
              title={!uploadedClips.some(c => c.type === 'video') ? 'Import a video or create one from script first' : processing ? `Rendering ${renderProgress}%` : 'Export video with current options'}
              aria-label={processing ? `Rendering ${renderProgress}%` : 'Export video'}
            >
              {processing ? `Rendering ${renderProgress}%…` : '🎬 Export Video'}
            </button>
          </div>
          </div>
          )}
        </div>

        {/* Center - Preview & Timeline */}
        <div className="editor-center">
          {/* Video Preview */}
          <div className="preview-section">
            <div className="preview-window">
              {hasVideoPreview ? (
                <>
                <video
                  ref={videoRef}
                    key={uploadedClips.find(c => c.type === 'video')?.url || uploadedClips[0]?.url}
                    src={uploadedClips.find(c => c.type === 'video')?.url || uploadedClips[0]?.url}
                  className="preview-video"
                  controls
                  playsInline
                    preload="auto"
                  onLoadedMetadata={(e) => {
                      const v = e.target;
                      const dur = v.duration;
                    if (dur && !isNaN(dur)) {
                      setDuration(dur);
                    }
                      v.playbackRate = playbackSpeed;
                    }}
                  onTimeUpdate={(e) => {
                    const t = e.target.currentTime;
                    if (!Number.isFinite(t)) return;
                    const now = Date.now();
                    if (now - timeUpdateThrottleRef.current >= 200) {
                      timeUpdateThrottleRef.current = now;
                      setCurrentTime(t);
                    }
                    // Sync audio tracks to video time
                    audioRefs.current.forEach(audio => {
                      if (audio && Math.abs(audio.currentTime - t) > 0.3) {
                        audio.currentTime = t;
                      }
                    });
                  }}
                    onPlay={() => {
                      setPlaying(true);
                      // Play all audio tracks
                      audioRefs.current.forEach(audio => {
                        if (audio) audio.play().catch(() => {});
                      });
                    }}
                    onPause={() => {
                      setPlaying(false);
                      // Pause all audio tracks
                      audioRefs.current.forEach(audio => {
                        if (audio) audio.pause();
                      });
                    }}
                    onEnded={() => {
                      setPlaying(false);
                      audioRefs.current.forEach(audio => {
                        if (audio) audio.pause();
                      });
                    }}
                    onSeeking={() => {
                      // Sync audio when seeking
                      const vt = videoRef.current?.currentTime;
                      if (!Number.isFinite(vt)) return;
                      audioRefs.current.forEach(audio => {
                        if (audio) audio.currentTime = vt;
                      });
                    }}
                  style={{
                    filter: `brightness(${effects.brightness}%) contrast(${effects.contrast}%) saturate(${effects.saturation}%)`,
                  }}
                />
                  {/* Hidden audio elements for music tracks */}
                  {uploadedClips
                    .filter(clip => clip.type === 'audio')
                    .map((audioClip, idx) => {
                      const trackWithClip = tracks.find(t => t.clips.some(c => c.id === audioClip.id));
                      const muted = trackWithClip && trackMuted[trackWithClip.id];
                      const vol = muted ? 0 : (audioVolumes[audioClip.id] ?? 0.7);
                      return (
                      <audio
                        key={audioClip.id}
                        ref={el => {
                          if (el) {
                            audioRefs.current[idx] = el;
                            el.volume = vol;
                          }
                        }}
                        src={audioClip.url}
                        preload="auto"
                        style={{ display: 'none' }}
                        onLoadedMetadata={(e) => {
                          e.target.playbackRate = playbackSpeed;
                          e.target.volume = vol;
                        }}
                      />
                    ); })}
                </>
              ) : (
                <div className="preview-placeholder">
                  <div className="placeholder-icon">🎬</div>
                  <p>Import video or create one from script</p>
                  <p className="hint">Drag and drop here or use Import in the sidebar. Use <strong>Create video from script</strong> (typing bar) for AI video — the placeholder hides when a video is on the preview.</p>
                  {uploadedClips.length > 0 && (
                    <p className="hint" style={{ marginTop: 4 }}>You have audio on the timeline — add a video file or generate a video to see it here.</p>
                  )}
                </div>
              )}
              {multiCamMode && (
                <div className="multicam-indicator">
                  🎥 Multi-Cam Mode: {syncedCameras.length} cameras synced
                </div>
              )}
              {uploadedClips.filter(c => c.type === 'audio').length > 0 && (
                <div className="multicam-indicator" style={{background: 'rgba(102, 126, 234, 0.9)', bottom: 'auto', top: '10px'}}>
                  🎵 Audio Mixing: {uploadedClips.filter(c => c.type === 'audio').length} track(s) {playing ? '(Playing)' : ''}
                </div>
              )}
            </div>
          </div>

          {/* Timeline - collapsible via + / − */}
          <div className={`timeline-wrapper ${timelineOpen ? 'timeline-wrapper-open' : 'timeline-wrapper-collapsed'}`}>
            {timelineOpen ? (
              <div
                className="timeline-section"
                onWheel={(e) => {
                  if (!e.ctrlKey && !e.metaKey) return;
                  e.preventDefault();
                  setZoom(z => Math.max(0.5, Math.min(5, z + (e.deltaY > 0 ? -0.25 : 0.25))));
                }}
              >
                <div className="timeline-toolbar">
                  <button
                    type="button"
                    className="timeline-collapse-btn"
                    onClick={() => setTimelineOpen(false)}
                    title="Hide timeline"
                    aria-label="Hide timeline"
                  >
                    −
                  </button>
                  <span className="toolbar-sep" aria-hidden="true">|</span>
              <button type="button" onClick={undo} disabled={undoCount === 0} title="Undo (Ctrl+Z)" className="undo-btn">
                ↩ Undo
              </button>
              <button type="button" onClick={redo} disabled={redoCount === 0} title="Redo (Ctrl+Shift+Z or Ctrl+Y)" className="redo-btn">
                ↪ Redo
              </button>
              <span className="toolbar-sep" aria-hidden="true">|</span>
              <button type="button" className={selectedTool === 'select' ? 'active' : ''} onClick={() => setSelectedTool('select')} title="Select clips" aria-label="Select tool">
                ➤ Select
              </button>
              <button type="button" className={selectedTool === 'cut' ? 'active' : ''} onClick={() => setSelectedTool('cut')} title="Cut tool" aria-label="Cut tool">
                ✂ Cut
              </button>
              <button
                type="button"
                className="split-btn"
                onClick={splitClipAtPlayhead}
                disabled={!selectedClip || currentTime <= (selectedClip.startTime ?? 0) || currentTime >= (selectedClip.startTime ?? 0) + (selectedClip.clipDuration ?? selectedClip.duration ?? 0)}
                title="Split selected clip at playhead (S)"
                aria-label="Split clip at playhead"
              >
                ✂ Split at playhead
              </button>
              <button type="button" className={selectedTool === 'trim' ? 'active' : ''} onClick={() => setSelectedTool('trim')} title="Trim tool" aria-label="Trim tool">
                ⇿ Trim
              </button>
              <button
                type="button"
                className="delete-clip-btn"
                onClick={deleteSelectedClip}
                disabled={!selectedClip}
                title="Remove selected clip (Delete / Backspace)"
                aria-label="Remove selected clip"
              >
                🗑 Remove clip
              </button>
              <div className="zoom-controls" role="group" aria-label="Timeline zoom">
                <button type="button" onClick={() => setZoom(Math.max(0.5, zoom - 0.25))} title="Zoom out" aria-label="Zoom out">-</button>
                <span aria-hidden="true">{Math.round(zoom * 100)}%</span>
                <button type="button" onClick={() => setZoom(Math.min(5, zoom + 0.25))} title="Zoom in" aria-label="Zoom in">+</button>
                <button type="button" className="fit-timeline-btn" onClick={fitTimelineToContent} title="Zoom timeline to fit all clips" aria-label="Fit timeline to content">
                  Fit
                </button>
              </div>
            </div>

            {/* Timeline ruler: time labels above tracks */}
            <div className="timeline-ruler">
              <div className="track-ruler-header">Time</div>
              <div
                className="track-ruler-content"
                style={{ minWidth: Math.max(1000, (Number.isFinite(duration) ? duration : 180) * zoom * 10) }}
              >
                {Array.from({ length: Math.min(500, Math.max(1, Math.ceil((Number.isFinite(duration) ? duration : 180) / 30) + 1)) }, (_, i) => i * 30)
                  .filter(t => t <= (Number.isFinite(duration) ? duration : 180))
                  .map(t => (
                    <div
                      key={t}
                      className="track-ruler-tick"
                      style={{ left: `${t * zoom * 10}px` }}
                    >
                      {Math.floor(t / 60)}:{String(Math.floor(t % 60)).padStart(2, '0')}
                    </div>
                  ))}
              </div>
            </div>

            <div className="timeline-tracks">
              {tracks.map((track, trackIndex) => (
                <div key={track.id} className="track">
                  <div className="track-header">
                    <span className="track-icon">
                      {track.type === 'video' ? '🎥' : track.type === 'audio' ? '🎵' : '📝'}
                    </span>
                    <span className="track-name">{track.name}</span>
                    <div className="track-controls">
                      {track.type === 'audio' && (
                        <button
                          onClick={() => setTrackMuted(prev => ({ ...prev, [track.id]: !prev[track.id] }))}
                          title={trackMuted[track.id] ? 'Unmute track' : 'Mute track'}
                          aria-label={trackMuted[track.id] ? 'Unmute track' : 'Mute track'}
                          className={trackMuted[track.id] ? 'muted' : ''}
                        >
                          {trackMuted[track.id] ? '🔇' : '🔊'}
                        </button>
                      )}
                      <button type="button" onClick={() => {
                        setTracks(tracks.map(t => t.id === track.id ? { ...t, visible: !t.visible } : t));
                      }} title={track.visible ? 'Hide track' : 'Show track'} aria-label={track.visible ? 'Hide track' : 'Show track'}>
                        {track.visible ? '👁' : '🚫'}
                      </button>
                    </div>
                  </div>
                  <div
                    ref={trackIndex === 0 ? timelineTrackContentRef : undefined}
                    className="track-content"
                    onClick={(e) => {
                      if (e.target.closest('.clip')) return;
                      const el = e.currentTarget;
                      const rect = el.getBoundingClientRect();
                      const x = e.clientX - rect.left + el.scrollLeft;
                      const t = x / (zoom * 10);
                      if (Number.isFinite(t)) seekTo(Math.max(0, Math.min(t, duration)));
                    }}
                    title="Click empty area to seek"
                    onDrop={(e) => {
                      e.preventDefault();
                      const clipData = e.dataTransfer.getData('clip');
                      if (!clipData) return;
                      try {
                        const clip = JSON.parse(clipData);
                        if (clip && track.type === (clip.type || 'video')) addClipToTrack(clip, track.id);
                      } catch (_) {
                        /* ignore invalid or empty drop (e.g. file drop from OS) */
                      }
                    }}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <div
                      className="timeline-playhead"
                      style={{ left: `${currentTime * zoom * 10}px` }}
                      aria-hidden="true"
                    />
                    {track.clips.length === 0 && trackIndex === 0 && (
                      <div className="timeline-empty-hint" aria-hidden="true">
                        Drag clips from Media Library onto a track
                      </div>
                    )}
                    {track.clips.map(clip => {
                      const clipDur = clip.clipDuration ?? clip.duration ?? 10;
                      const w = Math.max(8, clipDur * zoom * 10);
                      return (
                      <div
                        key={clip.id}
                        className={`clip ${selectedClip?.id === clip.id ? 'selected' : ''}`}
                        style={{
                          left: `${(clip.startTime ?? 0) * zoom * 10}px`,
                          width: `${w}px`,
                          minWidth: '8px',
                        }}
                        onClick={() => setSelectedClip(clip)}
                        onDoubleClick={() => {
                          const start = clip.startTime ?? 0;
                          if (Number.isFinite(start)) seekTo(start);
                        }}
                        onMouseDown={(e) => handleClipDragStart(e, clip, track.id)}
                        title="Drag to reposition (snap 0.5s); double-click to seek; Ctrl+C copy, Ctrl+V paste"
                      >
                        <span className="clip-name">{clip.name}</span>
                        {w >= 40 && (
                          <span className="clip-duration">
                            {Math.floor(clipDur)}s
                          </span>
                        )}
                      </div>
                    ); })}
                  </div>
                </div>
              ))}
            </div>
          </div>
            ) : (
              <button
                type="button"
                className="timeline-toggle-expand"
                onClick={() => setTimelineOpen(true)}
                title="Show timeline"
                aria-label="Show timeline"
              >
                + Timeline
              </button>
            )}
          </div>

        {/* Bottom strip: playback + typing bar pinned to bottom of page */}
        <div className="editor-bottom-strip">
        <div className="playback-controls" role="group" aria-label="Playback">
          <button type="button" onClick={() => seekTo(0)} disabled={!uploadedClips.some(c => c.type === 'video')} title="Go to start" aria-label="Go to start">⏮⏮</button>
          <button type="button" onClick={skipBackward} disabled={!uploadedClips.some(c => c.type === 'video')} title="Back 5s" aria-label="Skip backward 5 seconds">⏮</button>
          <button type="button" onClick={togglePlay} className="play-btn" disabled={!uploadedClips.some(c => c.type === 'video')} aria-label={playing ? 'Pause' : 'Play'} title={uploadedClips.some(c => c.type === 'video') ? (playing ? 'Pause' : 'Play') : 'Import or generate a video first'}>
            {playing ? '⏸' : '▶'}
          </button>
          <button type="button" onClick={skipForward} disabled={!uploadedClips.some(c => c.type === 'video')} title="Forward 5s" aria-label="Skip forward 5 seconds">⏭</button>
          <button type="button" onClick={() => seekTo(duration)} disabled={!uploadedClips.some(c => c.type === 'video')} title="Go to end" aria-label="Go to end">⏭⏭</button>
          <label className="playback-speed-label" title="Playback speed">
            <select
              value={playbackSpeed}
              onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
              className="playback-speed-select"
              title="Playback speed"
              disabled={!uploadedClips.some(c => c.type === 'video')}
              aria-label="Playback speed"
            >
              <option value={0.25}>0.25×</option>
              <option value={0.5}>0.5×</option>
              <option value={0.75}>0.75×</option>
              <option value={1}>1×</option>
              <option value={1.25}>1.25×</option>
              <option value={1.5}>1.5×</option>
              <option value={1.75}>1.75×</option>
              <option value={2}>2×</option>
            </select>
          </label>
          <div className="timeline-scrubber">
            <input
              type="range"
              min="0"
              max={Math.max(0.1, Number.isFinite(duration) ? duration : 180)}
              step={0.1}
              value={Number.isFinite(currentTime) ? currentTime : 0}
              onChange={(e) => seekTo(Number(e.target.value))}
              className="scrubber"
              disabled={!uploadedClips.some(c => c.type === 'video')}
              aria-label="Seek timeline"
              title="Drag to seek"
            />
            {markers.map(marker => (
              <div
                key={marker.id ?? `t-${marker.time}`}
                className="marker"
                style={{ left: `${(Number(marker.time) / (Number.isFinite(duration) && duration > 0 ? duration : 180)) * 100}%` }}
                title={marker.label ? `${marker.label} – click to seek, right‑click to remove` : 'Click to seek, right‑click to remove'}
                onClick={(e) => { e.stopPropagation(); seekTo(marker.time); }}
                onContextMenu={(e) => { e.preventDefault(); setMarkers(prev => prev.filter(m => (m.id ?? `t-${marker.time}`) !== (marker.id ?? `t-${marker.time}`))); }}
              >
                ⭐
                {marker.label && <span className="marker-label">{marker.label}</span>}
              </div>
            ))}
          </div>
          <button
            type="button"
            className="add-marker-btn"
            onClick={() => setMarkers(prev => [...prev, { id: `marker-${Date.now()}`, time: currentTime, label: `Marker ${prev.length + 1}` }])}
            disabled={!uploadedClips.some(c => c.type === 'video')}
            title="Add marker (M)"
            aria-label="Add marker"
          >
            ⭐
          </button>
        </div>

        {/* Typing bar at bottom: input + Send + "+" opens glassmorphism options (Model, Voice, Create video) */}
        <div className="ai-typing-bar-wrap">
          {typingOptionsOpen && (
            <div className="ai-typing-options-glass" ref={typingOptionsRef} role="dialog" aria-label="Script & model options">
              <div className="ai-typing-options-header">
                <span>Model &amp; script options</span>
                <button type="button" className="ai-typing-options-close" onClick={() => setTypingOptionsOpen(false)} aria-label="Close">−</button>
              </div>
              <div className="ai-typing-options-body">
                <label className="ai-typing-bar-option">
                  <span>Model</span>
                  <select
                    value={scriptToVideoModel}
                    onChange={(e) => setScriptToVideoModel(e.target.value)}
                    disabled={aiProcessing}
                    className="ai-typing-bar-select"
                    aria-label="Script-to-video model"
                  >
                    <option value="veo3">Veo 3 (VO3)</option>
                    <option value="tts">TTS + static visual</option>
                  </select>
                </label>
                <label className="ai-typing-bar-option">
                  <span>Voice</span>
                  <select
                    value={scriptToVideoVoice}
                    onChange={(e) => setScriptToVideoVoice(e.target.value)}
                    disabled={aiProcessing}
                    className="ai-typing-bar-select"
                    aria-label="TTS voice"
                  >
                    <option value="alloy">Alloy</option>
                    <option value="echo">Echo</option>
                    <option value="fable">Fable</option>
                    <option value="onyx">Onyx</option>
                    <option value="nova">Nova</option>
                    <option value="shimmer">Shimmer</option>
                  </select>
                </label>
                <button
                  type="button"
                  className="ai-typing-bar-create-btn"
                  disabled={aiProcessing || !aiInput.trim()}
                  onClick={() => {
                    const text = aiInput.trim().slice(0, 4096);
                    if (text) {
                      processAICommand(`Create video from this script:\n\n${text}`);
                      setAiInput('');
                      setTypingOptionsOpen(false);
                    }
                  }}
                  title="Turn the text above into a video (AI)"
                  aria-label="Create video from script"
                >
                  🎬 Create video from script
                </button>
              </div>
            </div>
          )}
          <form className="ai-typing-bar" onSubmit={handleAISubmit}>
            <input
              ref={aiInputRef}
              type="text"
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              placeholder="Paste script or ask AI to edit your video..."
              className="ai-typing-bar-input"
              disabled={aiProcessing}
              aria-label="Script or message to AI"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (aiInput.trim()) handleAISubmit(e);
                }
              }}
            />
            <button type="submit" className="ai-typing-bar-send" disabled={aiProcessing || !aiInput.trim()} aria-label="Send" title="Send to AI">
              ➤
            </button>
            <button
              type="button"
              className="ai-typing-bar-toggle-btn"
              onClick={() => setTypingOptionsOpen(!typingOptionsOpen)}
              title={typingOptionsOpen ? 'Close options' : 'Model, Voice & Create video from script'}
              aria-label={typingOptionsOpen ? 'Close options' : 'Open options'}
              aria-expanded={typingOptionsOpen}
            >
              +
            </button>
          </form>
        </div>
        </div>
      </div>
      </div>

      {/* Processing Overlay */}
      {processing && (
        <div className="processing-overlay">
          <div className="processing-spinner"></div>
          <p>AI is processing your video… {renderProgress > 0 && renderProgress < 100 ? `${Math.round(renderProgress)}%` : ''}</p>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${renderProgress}%` }}></div>
          </div>
        </div>
      )}
    </div>
  );
}
